import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, XCircle } from 'lucide-react';

interface MissingSelection {
  day: number;
  mealType: string;
}

interface MealSelectionValidationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  missingSelections: MissingSelection[];
  onCancel: () => void;
}

const MealSelectionValidationModal: React.FC<MealSelectionValidationModalProps> = ({
  open,
  onOpenChange,
  missingSelections,
  onCancel
}) => {
  const getMealTypeIcon = (mealType: string) => {
    switch (mealType.toLowerCase()) {
      case 'breakfast':
        return '🍳';
      case 'lunch':
        return '🥗';
      case 'dinner':
        return '🍽️';
      case 'snacks':
        return '🍎';
      default:
        return '🍴';
    }
  };

  const formatMissingSelections = (selections: MissingSelection[]) => {
    const groupedByDay = selections.reduce((acc, selection) => {
      if (!acc[selection.day]) {
        acc[selection.day] = [];
      }
      acc[selection.day].push(selection.mealType);
      return acc;
    }, {} as Record<number, string[]>);

    return Object.entries(groupedByDay).map(([day, mealTypes]) => ({
      day: parseInt(day),
      mealTypes
    }));
  };

  const formattedSelections = formatMissingSelections(missingSelections);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            Incomplete Meal Selection
          </DialogTitle>
          <DialogDescription>
            Please select exactly one meal for each meal type before saving the plan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              You have {missingSelections.length} missing meal selection{missingSelections.length !== 1 ? 's' : ''}.
            </AlertDescription>
          </Alert>

          <div className="space-y-3">
            <h4 className="font-medium text-sm">Missing selections:</h4>
            {formattedSelections.map(({ day, mealTypes }) => (
              <div key={day} className="bg-gray-50 rounded-lg p-3">
                <div className="font-medium text-sm mb-2">Day {day}</div>
                <div className="flex flex-wrap gap-2">
                  {mealTypes.map((mealType) => (
                    <div key={mealType} className="flex items-center gap-1 text-sm text-gray-600">
                      <span>{getMealTypeIcon(mealType)}</span>
                      <span className="capitalize">{mealType}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter className="flex justify-end">
          <Button variant="outline" onClick={onCancel}>
            Continue Editing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MealSelectionValidationModal;
